import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useToast,
  useDisclosure,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Card,
  SelectInput,
  Button,
  DataTable,
} from '@grcs/components';
import { TableColumn } from '@grcs/components/types';
import hanzi_list from '../dictionary/hanzi_pinyin_list.json';
import hsk_list from '../dictionary/hsk_pinyin_list.json';

interface VocabularyEntry {
  character: string;
  pinyin: string[];
  meaning?: string;
  grade: string;
  chapter: string;
  frequency?: number;
}

type ViewMode = 'grid' | 'table' | 'cards';
type DataSource = 'grcs' | 'hsk';

export const VocabularyEnhanced: React.FC = () => {
  const toast = useToast();
  const {
    isOpen: isCharModalOpen,
    onOpen: onCharModalOpen,
    onClose: onCharModalClose,
  } = useDisclosure();

  // UI State
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [dataSource, setDataSource] = useState<DataSource>('grcs');
  const [expandedGrades, setExpandedGrades] = useState<Set<string>>(new Set());

  // Process vocabulary data
  const vocabularyData = useMemo(() => {
    const entries: VocabularyEntry[] = [];

    Object.entries(hanzi_list).forEach(([gradeChapter, characters]) => {
      const [, chapter] = gradeChapter.split('.');

      if (typeof characters === 'object' && characters !== null) {
        Object.entries(characters as Record<string, string[]>).forEach(
          ([char, pinyin]) => {
            entries.push({
              character: char,
              pinyin: pinyin,
              grade: gradeChapter,
              chapter: chapter || '1',
              frequency: Math.floor(Math.random() * 100) + 1, // Mock frequency data
            });
          },
        );
      }
    });

    return entries;
  }, []);

  const hskData = useMemo(() => {
    const entries: VocabularyEntry[] = [];

    Object.entries(hsk_list).forEach(([gradeChapter, characters]) => {
      const [, chapter] = gradeChapter.split('.');

      if (typeof characters === 'object' && characters !== null) {
        Object.entries(characters as Record<string, string[]>).forEach(
          ([char, pinyin]) => {
            entries.push({
              character: char,
              pinyin: pinyin,
              grade: gradeChapter,
              chapter: chapter || '1',
              frequency: Math.floor(Math.random() * 100) + 1, // Mock frequency data
            });
          },
        );
      }
    });

    return entries;
  }, []);

  const activeVocabularyData = useMemo(() => {
    return dataSource === 'hsk' ? hskData : vocabularyData;
  }, [dataSource, hskData, vocabularyData]);

  useEffect(() => {
    setExpandedGrades(new Set());
  }, [dataSource]);

  const handleExpandGrade = useCallback((grade: string) => {
    setExpandedGrades((prev) => {
      const next = new Set(prev);
      next.add(grade);
      return next;
    });
  }, []);

  // Export vocabulary
  const handleExport = useCallback(() => {
    const csvContent = [
      ['Character', 'Pinyin', 'Grade', 'Chapter'].join(','),
      ...activeVocabularyData.map((entry) =>
        [entry.character, entry.pinyin, entry.grade, entry.chapter].join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `vocabulary-${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'Export successful',
      description: `Exported ${activeVocabularyData.length} vocabulary entries.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }, [activeVocabularyData, toast]);

  // Group vocabulary by grade
  const vocabularyByGrade = useMemo(() => {
    const grouped = new Map<string, VocabularyEntry[]>();

    activeVocabularyData.forEach((entry) => {
      if (!grouped.has(entry.grade)) {
        grouped.set(entry.grade, []);
      }
      grouped.get(entry.grade)!.push(entry);
    });

    return grouped;
  }, [activeVocabularyData]);

  // Table columns for table view
  const tableColumns: TableColumn<VocabularyEntry>[] = [
    {
      key: 'grade',
      header: 'Grade & Chapter',
      render: (entry) => (
        <Badge colorScheme="blue" size="sm">
          {entry.grade}
        </Badge>
      ),
    },
  ];

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              Vocabulary
            </Text>
          </Box>
          <HStack spacing={3}>
            <Box minW="200px">
              <SelectInput
                value={dataSource}
                onChange={(value) => setDataSource(value as DataSource)}
                options={[
                  { value: 'grcs', label: 'GRCS' },
                  { value: 'hsk', label: 'HSK' },
                ]}
                size="sm"
              />
            </Box>
            {/* <Button
              leftIcon={<FiDownload />}
              onClick={handleExport}
              variant="secondary"
              size="sm"
            >
              Export
            </Button> */}
          </HStack>
        </HStack>

        {/* Main Content */}
        <Tabs variant="line" colorScheme="blue">
          <TabList>
            <Tab>{dataSource === 'grcs' ? 'Grade' : 'Level'}</Tab>
            <Tab>All ({activeVocabularyData.length})</Tab>
          </TabList>

          <TabPanels>
            {/* By Grade Level Tab */}
            <TabPanel px={0}>
              <Accordion allowMultiple>
                {Array.from(vocabularyByGrade.entries()).map(
                  ([grade, entries]) => {
                    const isExpanded = expandedGrades.has(grade);
                    const visibleEntries = isExpanded
                      ? entries
                      : entries.slice(0, 50);

                    return (
                      <AccordionItem key={grade}>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <HStack justify="space-between">
                              <Text fontWeight="bold">
                                {dataSource === 'grcs' ? 'Grade' : 'Level'}{' '}
                                {grade}
                              </Text>
                              <Badge colorScheme="blue">{entries.length}</Badge>
                            </HStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <Grid
                            templateColumns="repeat(auto-fill, minmax(80px, 1fr))"
                            gap={3}
                          >
                            {visibleEntries.map((entry, index) => (
                              <Box
                                key={`${entry.character}-${index}`}
                                textAlign="center"
                                p={2}
                                borderWidth={1}
                                borderRadius="md"
                                cursor="pointer"
                                _hover={{ bg: 'blue.50' }}
                                // onClick={() => handleCharacterSelect(entry)}
                              >
                                <Text
                                  fontSize="2xl"
                                  fontWeight="bold"
                                  color="blue.600"
                                >
                                  {entry.character}
                                </Text>
                                <Text fontSize="xs" color="gray.600">
                                  {entry.pinyin.join(' ')}
                                </Text>
                              </Box>
                            ))}
                          </Grid>
                          {entries.length > 50 && !isExpanded && (
                            <HStack justify="center" mt={4}>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleExpandGrade(grade)}
                              >
                                Show all {entries.length}
                              </Button>
                            </HStack>
                          )}
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  },
                )}
              </Accordion>
            </TabPanel>

            {/* Main Vocabulary Tab */}
            <TabPanel px={0}>
              {viewMode === 'grid' ? (
                <Grid
                  templateColumns="repeat(auto-fill, minmax(120px, 1fr))"
                  gap={4}
                >
                  {activeVocabularyData.map((entry, index) => (
                    <GridItem key={`${entry.character}-${index}`}>
                      <Card
                        _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
                        cursor="pointer"
                        // onClick={() => handleCharacterSelect(entry)}
                        textAlign="center"
                        p={4}
                      >
                        <VStack spacing={2}>
                          <HStack justify="center" spacing={2}>
                            <Text
                              fontSize="3xl"
                              fontWeight="bold"
                              color="blue.600"
                            >
                              {entry.character}
                            </Text>
                          </HStack>
                          <Text
                            fontSize="sm"
                            fontFamily="monospace"
                            color="gray.600"
                          >
                            {entry.pinyin.join(' ')}
                          </Text>
                        </VStack>
                      </Card>
                    </GridItem>
                  ))}
                </Grid>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  {activeVocabularyData.map((entry, index) => (
                    <Card
                      key={`${entry.character}-${index}`}
                      _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
                      cursor="pointer"
                      // onClick={() => handleCharacterSelect(entry)}
                    >
                      <HStack spacing={4} align="center">
                        <VStack spacing={1} align="center" minW="80px">
                          <Text
                            fontSize="4xl"
                            fontWeight="bold"
                            color="blue.600"
                          >
                            {entry.character}
                          </Text>
                          <Badge size="sm" colorScheme="blue">
                            {entry.grade}
                          </Badge>
                        </VStack>
                      </HStack>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Character Detail Modal
        <Modal
          isOpen={isCharModalOpen}
          onClose={onCharModalClose}
          size="lg"
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              Character Details: {selectedCharacter?.character}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedCharacter && (
                <VStack spacing={6} align="stretch">
                  <HStack justify="center" spacing={8}>
                    <VStack>
                      <Text fontSize="6xl" fontWeight="bold" color="blue.600">
                        {selectedCharacter.character}
                      </Text>
                    </VStack>

                    <VStack spacing={4} align="start">
                      <Stat>
                        <StatLabel>Grade & Chapter</StatLabel>
                        <StatNumber fontSize="lg">
                          <Badge colorScheme="blue">
                            {selectedCharacter.grade}
                          </Badge>
                        </StatNumber>
                        <StatHelpText>Learning level</StatHelpText>
                      </Stat>

                      <Stat>
                        <StatLabel>Usage Frequency</StatLabel>
                        <StatNumber fontSize="lg">
                          {selectedCharacter.frequency}%
                        </StatNumber>
                        <StatHelpText>In modern Chinese</StatHelpText>
                      </Stat>
                    </VStack>
                  </HStack>

                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Character Information
                    </Text>
                    <VStack spacing={2} align="start">
                      <Text fontSize="sm">
                        <strong>Traditional:</strong>{' '}
                        {selectedCharacter.character} (Same as simplified)
                      </Text>
                      <Text fontSize="sm">
                        <strong>Strokes:</strong>{' '}
                        {Math.floor(Math.random() * 20) + 1} strokes
                      </Text>
                      <Text fontSize="sm">
                        <strong>HSK Level:</strong> HSK{' '}
                        {selectedCharacter.grade.split('.')[0]}
                      </Text>
                    </VStack>
                  </Box>
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal> */}
      </VStack>
    </AdminLayout>
  );
};
